import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router";
import { ChevronDown, ChevronRight, ChevronsDownUp, ChevronsUpDown, CircleCheck, CircleDashed, CircleX, Pause, Pencil, Play, Plus, RefreshCw, Search, Trash2, TriangleAlert, X } from "lucide-react";

import { DataTable } from "../components/DataTable.jsx";
import { PageHeader } from "../components/PageHeader.jsx";
import { ProjectIcon } from "../components/ProjectIcon.jsx";
import { Sparkline } from "../components/Sparkline.jsx";
import { StatSummary } from "../components/StatSummary.jsx";
import { StatusBadge, StatusDot } from "../components/StatusBadge.jsx";
import { EmptyState, Modal, Spinner, ago, useToast } from "../components/ui.jsx";
import { api } from "../lib/api.js";
import { useLive } from "../lib/events.js";
import { useUrlStates } from "../lib/useUrlState.js";

/**
 * Is everything answering — the whole box on one page.
 *
 * Every PM2 process, every domain nginx serves, the database, and whatever else
 * was asked to be watched, grouped by the project each belongs to: what it said
 * when last asked, how long it has said that, and the ninety days behind it as
 * one bar per day. A component that is not answering is red, one that missed
 * once and has not yet been confirmed is amber, and the page's own headline is
 * the same dot the nav carries.
 *
 * One row per project, closed until opened. Fifteen projects as open cards was
 * a page nobody could take in; a row carries what you need to decide whether to
 * open it — the dot, who is down, and the last ten days — and the components
 * are inside. Searching or filtering opens whatever matches, because then the
 * point is to see it.
 *
 * The bars are answered-over-asked, never an SLA figure: a day where ops itself
 * was not running is gray, not green, and a single missed probe shows as amber
 * with the count in its tooltip.
 */

const DAY_MS = 86400000;

const KIND_LABEL = { http: "http", tcp: "tcp", sip: "sip", mongo: "mongo", pm2: "pm2", site: "site" };

function percent(value) {
    if (value === null || value === undefined) return "—";
    return `${value === 100 ? "100" : value.toFixed(2)}%`;
}

function humanDuration(ms) {
    const seconds = Math.max(0, Math.round(ms / 1000));
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ${String(seconds % 60).padStart(2, "0")}s`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ${String(minutes % 60).padStart(2, "0")}m`;
    return `${Math.floor(hours / 24)}d ${hours % 24}h`;
}

// --- a day ----------------------------------------------------------------------

/** The UTC day a bar stands for: its start and end, the end clipped to now. */
function dayWindow(index, total, now) {
    const start = new Date(now - (total - 1 - index) * DAY_MS);
    start.setUTCHours(0, 0, 0, 0);
    return { start: start.getTime(), end: Math.min(start.getTime() + DAY_MS, now) };
}

/**
 * What a day was, from its totals and the incidents that touched it.
 *
 * An incident colours the day red however short it was — that is what the bar
 * is for. Misses that never became an incident are amber: something blipped
 * and nobody was told. Green answered everything. Gray was never asked.
 */
function dayOf(bar, incidents, index, total, now) {
    const { start, end } = dayWindow(index, total, now);
    const date = new Date(start);
    const related = [];
    let downMs = 0;
    for (const incident of incidents) {
        const from = new Date(incident.startedAt).getTime();
        const to = incident.endedAt ? new Date(incident.endedAt).getTime() : now;
        const overlap = Math.min(to, end) - Math.max(from, start);
        if (overlap <= 0) continue;
        downMs += overlap;
        related.push({ ...incident, from: Math.max(from, start), to: Math.min(to, end), ongoing: !incident.endedAt });
    }
    if (related.length > 0) return { date, kind: "outage", downMs, bar, related };
    if (!bar) return { date, kind: "none", bar: null, related };
    return { date, kind: bar[1] > 0 ? "missed" : "ok", bar, related };
}

const KIND = {
    outage: { label: "Outage", tone: "bg-red-500", Icon: CircleX, icon: "text-red-500" },
    missed: { label: "Missed checks", tone: "bg-amber-400", Icon: TriangleAlert, icon: "text-amber-500" },
    ok: { label: "Operational", tone: "bg-green-500", Icon: CircleCheck, icon: "text-green-500" },
    none: { label: "No data", tone: "bg-gray-200 dark:bg-gray-800", Icon: CircleDashed, icon: "text-gray-400" }
};

const WORSE = { outage: 3, missed: 2, ok: 1, none: 0 };

function clock(ms) {
    return new Date(ms).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function hoursAndMinutes(ms) {
    const minutes = Math.max(1, Math.round(ms / 60000));
    return `${Math.floor(minutes / 60)} hrs ${minutes % 60} mins`;
}

/**
 * The card a bar opens: the day, what it was and for how long, and the
 * incidents behind it. One for the whole page, drawn over everything from a
 * portal, because the rows live inside a card that clips its overflow.
 */
function DayPopover({ anchor, day }) {
    const ref = useRef(null);
    const [place, setPlace] = useState(null);

    useLayoutEffect(() => {
        const box = ref.current;
        if (!box || !anchor) return;
        const margin = 8;
        const width = box.offsetWidth;
        const x = anchor.left + anchor.width / 2;
        const left = Math.min(Math.max(margin, x - width / 2), window.innerWidth - width - margin);
        // Above the bar unless there is no room, in which case below it.
        const flip = anchor.top < box.offsetHeight + 24;
        setPlace({ left, caret: x - left, flip, top: flip ? anchor.bottom + 10 : null, bottom: flip ? null : window.innerHeight - anchor.top + 10 });
    }, [anchor, day]);

    if (!anchor || !day) return null;
    const meta = KIND[day.kind];
    const title = day.date.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
    const answered = day.bar ? day.bar[0] - day.bar[1] : 0;

    return createPortal(
        <div
            ref={ref}
            role="tooltip"
            data-day-popover=""
            style={{ left: place?.left ?? -9999, top: place?.top ?? undefined, bottom: place?.bottom ?? undefined, visibility: place ? "visible" : "hidden" }}
            className="fixed z-50 w-72 rounded-xl border border-gray-200 bg-white p-4 text-sm shadow-xl dark:border-gray-700 dark:bg-gray-900"
        >
            <span
                aria-hidden
                style={{ left: (place?.caret ?? 0) - 6 }}
                className={`absolute size-3 rotate-45 border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900 ${place?.flip ? "-top-1.5 border-l border-t" : "-bottom-1.5 border-b border-r"}`}
            />
            <p className="font-semibold">{title}</p>
            <div className="mt-3 flex items-center gap-2 rounded-lg bg-gray-100 px-3 py-2 dark:bg-gray-800">
                <meta.Icon size={16} className={`shrink-0 ${meta.icon}`} aria-hidden />
                <span className="font-medium">{meta.label}</span>
                <span className="muted ml-auto shrink-0 text-xs tabular-nums">
                    {day.kind === "outage"
                        ? hoursAndMinutes(day.downMs)
                        : day.kind === "missed"
                            ? `${day.bar[1]} of ${day.bar[0]} missed`
                            : day.kind === "ok"
                                ? `${answered.toLocaleString()} checks${answered > 0 ? ` · ${Math.round(day.bar[2] / answered)}ms` : ""}`
                                : "never asked"}
                </span>
            </div>
            {day.related.length > 0 ? (
                <div className="mt-3">
                    <p className="muted text-[10px] font-semibold uppercase tracking-wider">Related</p>
                    <ul className="mt-1.5 space-y-1.5">
                        {day.related.map((incident) => (
                            <li key={incident.id} className="text-xs">
                                <span className="tabular-nums">{clock(incident.from)} – {incident.ongoing && incident.to >= Date.now() - 60000 ? "now" : clock(incident.to)}</span>
                                {incident.name && incident.showName ? <span className="font-medium"> · {incident.name}</span> : null}
                                <span className="muted"> · {incident.reason}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            ) : null}
        </div>,
        document.body
    );
}

/** The bars of several components added together, day by day. */
function sumBars(components) {
    const days = components[0]?.bars.length ?? 0;
    return Array.from({ length: days }, (_, index) => {
        let total = null;
        for (const component of components) {
            const bar = component.bars[index];
            if (!bar) continue;
            total = total ? [total[0] + bar[0], total[1] + bar[1], total[2] + bar[2], Math.max(total[3], bar[3])] : [...bar];
        }
        return total;
    });
}

/** One bar per day, each opening the day's card on hover, focus or a tap. */
function Bars({ bars, incidents, now, onDay, hidden = 30 }) {
    const total = bars.length;
    return (
        <div className="flex h-6 gap-px" role="group" aria-label={`${total} days`} data-bars="" onMouseLeave={() => onDay(null)}>
            {bars.map((bar, index) => {
                const day = dayOf(bar, incidents, index, total, now);
                const show = (event) => onDay({ anchor: event.currentTarget.getBoundingClientRect(), day });
                // A phone shows the last month; the rest of the quarter needs the width.
                return (
                    <button
                        key={index}
                        type="button"
                        onMouseEnter={show}
                        onFocus={show}
                        onClick={show}
                        onBlur={() => onDay(null)}
                        aria-label={`${day.date.toLocaleDateString(undefined, { month: "short", day: "numeric" })}: ${KIND[day.kind].label}`}
                        className={`min-w-0 flex-1 rounded-sm ${KIND[day.kind].tone} ${index < total - hidden ? "hidden sm:block" : ""} hover:opacity-70 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500`}
                    />
                );
            })}
        </div>
    );
}

/** The last ten days of a whole project, small enough to sit in a row. The worst component colours the day. */
function MiniBars({ components, now, onDay }) {
    const bars = sumBars(components);
    const total = bars.length;
    const from = Math.max(0, total - 10);
    return (
        <div className="flex h-4 shrink-0 gap-px" role="group" aria-label="last 10 days" data-minibars="" onMouseLeave={() => onDay(null)}>
            {bars.slice(from).map((bar, offset) => {
                const index = from + offset;
                const days = components.map((component) => ({ component, day: dayOf(component.bars[index], component.incidents ?? [], index, total, now) }));
                const worst = days.reduce((best, entry) => (WORSE[entry.day.kind] > WORSE[best.day.kind] ? entry : best), days[0]);
                const related = days.flatMap((entry) => entry.day.related.map((incident) => ({ ...incident, showName: true })));
                const day = { ...worst.day, bar, downMs: days.reduce((sum, entry) => sum + (entry.day.downMs ?? 0), 0), related };
                const show = (event) => onDay({ anchor: event.currentTarget.getBoundingClientRect(), day });
                return (
                    <button
                        key={index}
                        type="button"
                        onMouseEnter={show}
                        onFocus={show}
                        onClick={show}
                        onBlur={() => onDay(null)}
                        aria-label={`${day.date.toLocaleDateString(undefined, { month: "short", day: "numeric" })}: ${KIND[day.kind].label}`}
                        className={`w-1.5 rounded-sm sm:w-2 ${KIND[day.kind].tone} hover:opacity-70 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500`}
                    />
                );
            })}
        </div>
    );
}

/** What the restart action is doing, or did, in a line. */
function restartText(restart, now) {
    const { attempts, of, running, startedAt, last } = restart;
    if (running) return `attempt ${attempts} of ${of}, running for ${humanDuration(now - new Date(startedAt))}`;
    if (last) return `attempt ${last.attempt} of ${of} ${last.ok ? "brought it back" : "did not help"} ${ago(last.at)}${last.output ? ` · ${last.output.split("\n").pop()}` : ""}`;
    return `over SSH, up to ${of} ${of === 1 ? "time" : "times"}, before it is said`;
}

function restartCount(restarts) {
    const back = restarts.some((tried) => tried.ok);
    const n = restarts.length;
    return back ? `back after ${n === 1 ? "one restart" : `${n} restarts`}` : `${n} restart${n === 1 ? "" : "s"} tried`;
}

function ComponentRow({ component, expanded, onToggle, onEdit, onDay, now }) {
    const Chevron = expanded ? ChevronDown : ChevronRight;
    const configured = component.id.startsWith("monitor:");
    const own = (component.incidents ?? []).slice(0, 5);

    return (
        <li className="px-4 py-3" data-component={component.id} data-state={component.state}>
            <button onClick={onToggle} className="flex w-full items-center gap-2 text-left" aria-expanded={expanded}>
                <Chevron size={14} className="muted shrink-0" aria-hidden />
                <StatusDot status={component.state} title={component.state} />
                <span className="min-w-0 flex-1 truncate text-sm font-medium">{component.name}</span>
                <span className="muted hidden font-mono text-[10px] uppercase sm:inline">{KIND_LABEL[component.kind] ?? component.kind}</span>
                <span className="muted hidden text-xs tabular-nums md:inline" title={component.probe}>{component.detail}</span>
                <span className="hidden sm:inline"><StatusBadge status={component.state}>{component.state === "restarting" ? `restarting ${component.restart.attempts}/${component.restart.of}` : undefined}</StatusBadge></span>
                <span className="w-16 text-right text-xs tabular-nums" title="Answered over asked, last 90 days">{percent(component.uptime.d90)}</span>
            </button>
            <div className="mt-2 pl-6">
                <Bars bars={component.bars} incidents={component.incidents ?? []} now={now} onDay={onDay} />
            </div>

            {expanded ? (
                <div className="mt-3 space-y-3 pl-6 text-sm">
                    <dl className="flex flex-wrap gap-x-5 gap-y-1 text-xs">
                        <div className="flex items-center gap-1.5"><dt className="muted">Since</dt><dd className="tabular-nums" title={component.since}>{component.since ? `${ago(component.since)} · ${humanDuration(now - new Date(component.since))}` : "—"}</dd></div>
                        <div className="flex items-center gap-1.5"><dt className="muted">Checked</dt><dd>{ago(component.checkedAt)}</dd></div>
                        <div className="flex items-center gap-1.5"><dt className="muted">Last answer</dt><dd className="tabular-nums">{component.detail || "—"}{component.error && component.error !== component.detail ? <span className="muted"> · {component.error}</span> : null}</dd></div>
                        <div className="flex items-center gap-1.5"><dt className="muted">Uptime</dt><dd className="tabular-nums">24h {percent(component.uptime.d1)} · 7d {percent(component.uptime.d7)} · 30d {percent(component.uptime.d30)}</dd></div>
                        {component.restart?.of > 0 ? (
                            <div className="flex min-w-0 basis-full items-center gap-1.5"><dt className="muted shrink-0">Restart</dt><dd className="min-w-0 truncate" title={component.restart.last?.output || undefined}>{restartText(component.restart, now)}</dd></div>
                        ) : null}
                        <div className="flex min-w-0 basis-full items-center gap-1.5"><dt className="muted shrink-0">Asked</dt><dd className="min-w-0 truncate font-mono" title={component.probe}>{component.probe}</dd></div>
                    </dl>

                    <div>
                        <p className="muted mb-1 text-xs">Latency, last 24 hours</p>
                        <Sparkline values={component.latency24h} tone="text-blue-500" />
                    </div>

                    {own.length > 0 ? (
                        <div>
                            <p className="muted mb-1 text-xs">Incidents</p>
                            <ul className="space-y-1">
                                {own.map((incident) => (
                                    <li key={incident.id} className="flex flex-wrap items-baseline gap-x-2 text-xs">
                                        <span className={incident.endedAt ? "muted" : "font-medium text-red-600 dark:text-red-400"}>{incident.endedAt ? "resolved" : "open"}</span>
                                        <span className="tabular-nums">{ago(incident.startedAt)}</span>
                                        <span className="muted">for {humanDuration(incident.durationMs)}</span>
                                        <span className="muted min-w-0 truncate">{incident.reason}</span>
                                        {incident.restarts?.length > 0 ? <span className="muted" title={incident.restarts.map((tried) => `${tried.attempt}: ${tried.ok ? "back" : tried.output || "failed"}`).join("\n")}>· {restartCount(incident.restarts)}</span> : null}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ) : null}

                    {configured ? (
                        <div className="flex flex-wrap gap-2">
                            <button className="btn-ghost" onClick={() => onEdit(component, "edit")}><Pencil size={13} /> Edit</button>
                            <button className="btn-ghost" onClick={() => onEdit(component, component.state === "paused" ? "resume" : "pause")}>
                                {component.state === "paused" ? <><Play size={13} /> Resume</> : <><Pause size={13} /> Pause</>}
                            </button>
                            <button className="btn-danger" onClick={() => onEdit(component, "delete")}><Trash2 size={13} /> Delete</button>
                        </div>
                    ) : null}
                </div>
            ) : null}
        </li>
    );
}

function GroupRow({ group, open, onToggle, component, onComponent, onEdit, onDay, now }) {
    const counted = group.components.filter((row) => row.state !== "paused");
    const up = counted.filter((row) => row.state === "up").length;
    const down = group.components.filter((row) => row.state === "down").map((row) => row.name);
    const missed = group.components.filter((row) => row.state === "failing").map((row) => row.name);
    const restarting = group.components.filter((row) => row.state === "restarting").map((row) => row.name);
    const trouble = [
        down.length > 0 ? `${down.join(", ")} ${down.length === 1 ? "is" : "are"} not answering` : null,
        restarting.length > 0 ? `${restarting.join(", ")} being restarted` : null,
        missed.length > 0 ? `${missed.join(", ")} missed once` : null
    ].filter(Boolean).join(" · ");
    const bars = sumBars(group.components);
    const Chevron = open ? ChevronDown : ChevronRight;

    return (
        <li data-group={group.key} data-open={open}>
            <div className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-900">
                <button onClick={onToggle} className="flex min-w-0 flex-1 items-center gap-3 text-left" aria-expanded={open} aria-label={`${group.label}, ${up} of ${counted.length} up`}>
                    <Chevron size={15} className="muted shrink-0" aria-hidden />
                    {group.slug ? <ProjectIcon slug={group.slug} hash={group.faviconHash} size={18} /> : <span className="size-[18px] shrink-0" aria-hidden />}
                    <span className="min-w-0 truncate text-sm font-semibold">{group.label}</span>
                    {trouble ? (
                        <span className={`hidden min-w-0 truncate text-xs sm:inline ${down.length > 0 ? "text-red-600 dark:text-red-400" : "text-amber-600 dark:text-amber-400"}`}>{trouble}</span>
                    ) : null}
                    <span className="muted ml-auto shrink-0 text-xs tabular-nums">{up}/{counted.length} up</span>
                </button>
                {/* Outside the toggle: each bar is a control of its own. */}
                <MiniBars components={group.components} now={now} onDay={onDay} />
                <span className="hidden w-16 shrink-0 text-right text-xs tabular-nums sm:inline" title="Answered over asked, last 90 days">{percent(uptimeOf(bars, 90))}</span>
                <StatusDot status={group.state} title={group.state} />
                {group.slug ? (
                    <Link to={`/projects/${group.slug}`} className="muted hidden shrink-0 text-xs hover:underline md:inline" aria-label={`Open the ${group.label} project`}>project →</Link>
                ) : null}
            </div>
            {open ? (
                <ul className="divide-y divide-gray-100 border-t border-gray-100 bg-gray-50/60 pl-4 sm:pl-8 dark:divide-gray-800 dark:border-gray-800 dark:bg-gray-900/40">
                    {group.components.map((row) => (
                        <ComponentRow
                            key={row.id}
                            component={row}
                            expanded={component === row.id}
                            onToggle={() => onComponent(component === row.id ? "" : row.id)}
                            onEdit={onEdit}
                            onDay={onDay}
                            now={now}
                        />
                    ))}
                </ul>
            ) : null}
        </li>
    );
}

/** Answered over asked across a strip of bars, for a whole project. */
function uptimeOf(bars, days) {
    let checks = 0;
    let failures = 0;
    for (const bar of bars.slice(-days)) {
        if (!bar) continue;
        checks += bar[0];
        failures += bar[1];
    }
    return checks === 0 ? null : Math.round(((checks - failures) / checks) * 10000) / 100;
}

const STATES = [
    { key: "", label: "All" },
    { key: "down", label: "Down", dot: "bg-red-500" },
    { key: "failing", label: "Missed", dot: "bg-amber-500" },
    { key: "paused", label: "Paused", dot: "bg-gray-400" }
];

/**
 * The search and the filter, as one line.
 *
 * The filter is a row of pills with a count each rather than a select: there are
 * three states worth narrowing to, and the count is the reason to click one.
 */
function Toolbar({ query, onQuery, state, onState, counts, allOpen, onToggleAll }) {
    return (
        <div className="flex flex-wrap items-center gap-2">
            {/* The search takes the line on a phone; the pills and the toggle share the next. */}
            <div className="relative min-w-0 basis-full sm:flex-auto sm:basis-56">
                <Search size={15} className="muted pointer-events-none absolute left-3 top-1/2 -translate-y-1/2" aria-hidden />
                <input
                    className="input rounded-lg pl-9 pr-16"
                    placeholder="Search a component or project"
                    value={query}
                    onChange={(event) => onQuery(event.target.value)}
                    aria-label="Search a component or project"
                    data-page-search=""
                />
                {query ? (
                    <button onClick={() => onQuery("")} className="muted absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 hover:text-gray-900 dark:hover:text-gray-100" aria-label="Clear the search">
                        <X size={14} />
                    </button>
                ) : (
                    <kbd className="pointer-events-none absolute right-3 top-1/2 hidden -translate-y-1/2 rounded border border-gray-300 px-1.5 font-sans text-[10px] text-gray-400 sm:inline dark:border-gray-700 dark:text-gray-500">/</kbd>
                )}
            </div>

            {/* Sized by its content, so a line that cannot hold it wraps rather than overflows. */}
            <div className="flex flex-auto items-center gap-2">
            <div className="flex shrink-0 rounded-lg border border-gray-200 bg-white p-0.5 dark:border-gray-800 dark:bg-gray-900" role="group" aria-label="Show">
                {STATES.map((option) => {
                    const active = state === option.key;
                    const count = counts[option.key || "all"] ?? 0;
                    return (
                        <button
                            key={option.key}
                            onClick={() => onState(active ? "" : option.key)}
                            aria-pressed={active}
                            data-state-filter={option.key || "all"}
                            disabled={!option.key ? false : count === 0 && !active}
                            className={`flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors disabled:opacity-40
                                ${active ? "bg-gray-900 text-white shadow-sm dark:bg-white dark:text-gray-900" : "text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"}`}
                        >
                            {option.dot ? <span className={`size-1.5 rounded-full ${option.dot}`} aria-hidden /> : null}
                            {option.label}
                            <span className={`tabular-nums ${active ? "opacity-70" : "muted"}`}>{count}</span>
                        </button>
                    );
                })}
            </div>

            <button onClick={onToggleAll} className="btn-ghost ml-auto shrink-0" aria-label={allOpen ? "Collapse every project" : "Expand every project"}>
                {allOpen ? <ChevronsDownUp size={14} /> : <ChevronsUpDown size={14} />}
                <span className="hidden lg:inline">{allOpen ? "Collapse all" : "Expand all"}</span>
            </button>
            </div>
        </div>
    );
}

function Banner({ data }) {
    const down = data.summary.down;
    const openSince = data.incidents.open.reduce((oldest, incident) => (!oldest || incident.startedAt < oldest ? incident.startedAt : oldest), null);
    const tone = data.state === "down"
        ? "border-red-200 bg-red-50 dark:border-red-900/60 dark:bg-red-950/30"
        : data.state === "operational"
            ? "border-green-200 bg-green-50 dark:border-green-900/60 dark:bg-green-950/30"
            : "border-gray-200 bg-gray-50 dark:border-gray-800 dark:bg-gray-900";
    return (
        <div className={`flex flex-wrap items-center gap-x-4 gap-y-1 rounded-xl border px-4 py-3 ${tone}`} data-banner={data.state}>
            <p className="flex min-w-0 items-center gap-2 text-sm font-medium">
                <StatusDot status={data.state === "operational" ? "up" : data.state === "down" ? "down" : "unknown"} />
                <span>
                    {data.state === "down"
                        ? `${down} component${down === 1 ? "" : "s"} not answering${openSince ? ` since ${ago(openSince)}` : ""}`
                        : data.state === "operational"
                            ? "All systems operational"
                            : "Collecting…"}
                </span>
            </p>
            <p className="muted ml-auto text-xs">
                {data.checkedAt ? `checked ${ago(data.checkedAt)}` : "not checked yet"}
                {data.degraded ? " · database unreachable, showing the last pass from memory" : ""}
            </p>
        </div>
    );
}

const EMPTY_FORM = {
    name: "", kind: "http", target: "", group: "", expectStatus: "", jsonPath: "", jsonEquals: "", headerName: "", headerValue: "", hasHeader: false,
    restarts: false, restartTarget: "", restartCommand: "", restartAttempts: "3"
};

/**
 * The form for a monitor. For HTTP it can carry an API key — sent as a header,
 * sealed at rest, never shown again — and a value the JSON answer must hold,
 * which is how a WhatsApp session that answers 200 while waiting for a QR code
 * is told apart from one that is working. Any kind can carry a restart: a
 * command run over SSH when it is down, a few times, before it is said.
 */
function MonitorForm({ initial, kinds, onSubmit, onCancel, busy }) {
    const [form, setForm] = useState(initial);
    const set = (key) => (event) => setForm((value) => ({ ...value, [key]: event.target.value }));
    const kind = kinds[form.kind] ?? {};

    return (
        <form
            className="space-y-3"
            onSubmit={(event) => {
                event.preventDefault();
                onSubmit(form);
            }}
        >
            <div>
                <label className="label" htmlFor="monitor-name">Name</label>
                <input id="monitor-name" className="input mt-1" value={form.name} onChange={set("name")} placeholder="WAHA" required maxLength={60} />
            </div>
            <div>
                <label className="label" htmlFor="monitor-kind">How to ask</label>
                <select id="monitor-kind" className="input mt-1" value={form.kind} onChange={set("kind")}>
                    {Object.entries(kinds).map(([id, meta]) => <option key={id} value={id}>{meta.label}</option>)}
                </select>
                {kind.hint ? <p className="muted mt-1 text-xs">{kind.hint}</p> : null}
            </div>
            <div>
                <label className="label" htmlFor="monitor-target">Target</label>
                <input id="monitor-target" className="input-mono mt-1" value={form.target} onChange={set("target")} placeholder={kind.placeholder ?? ""} required />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
                <div>
                    <label className="label" htmlFor="monitor-group">Group</label>
                    <input id="monitor-group" className="input mt-1" value={form.group} onChange={set("group")} placeholder="Tailnet" maxLength={40} />
                </div>
                {form.kind === "http" ? (
                    <div>
                        <label className="label" htmlFor="monitor-expect">Expected status <span className="muted font-normal">(optional)</span></label>
                        <input id="monitor-expect" className="input mt-1" inputMode="numeric" value={form.expectStatus} onChange={set("expectStatus")} placeholder="any under 500" />
                    </div>
                ) : null}
            </div>
            {form.kind === "http" ? (
                <>
                    <div className="grid gap-3 sm:grid-cols-2">
                        <div>
                            <label className="label" htmlFor="monitor-json-path">Value in the JSON <span className="muted font-normal">(optional)</span></label>
                            <input id="monitor-json-path" className="input-mono mt-1" value={form.jsonPath} onChange={set("jsonPath")} placeholder="status" maxLength={100} />
                        </div>
                        <div>
                            <label className="label" htmlFor="monitor-json-equals">must equal</label>
                            <input id="monitor-json-equals" className="input-mono mt-1" value={form.jsonEquals} onChange={set("jsonEquals")} placeholder="WORKING" maxLength={200} disabled={!form.jsonPath.trim()} />
                        </div>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2">
                        <div>
                            <label className="label" htmlFor="monitor-header-name">Header <span className="muted font-normal">(optional, for an API key)</span></label>
                            <input id="monitor-header-name" className="input-mono mt-1" value={form.headerName} onChange={set("headerName")} placeholder="X-Api-Key" maxLength={64} />
                        </div>
                        <div>
                            <label className="label" htmlFor="monitor-header-value">Value</label>
                            <input
                                id="monitor-header-value"
                                type="password"
                                autoComplete="off"
                                className="input-mono mt-1"
                                value={form.headerValue}
                                onChange={set("headerValue")}
                                placeholder={form.hasHeader ? "unchanged" : ""}
                                disabled={!form.headerName.trim()}
                            />
                            {form.hasHeader ? <p className="muted mt-1 text-xs">Sealed at rest; leave blank to keep it, clear the name to remove it.</p> : null}
                        </div>
                    </div>
                </>
            ) : null}
            <div className="border-t border-gray-200 pt-3 dark:border-gray-800">
                <label className="flex cursor-pointer items-start gap-2 text-sm">
                    <input id="restart-enabled" type="checkbox" className="mt-0.5 size-4 shrink-0" checked={form.restarts} onChange={(event) => setForm((value) => ({ ...value, restarts: event.target.checked }))} />
                    <span>
                        Restart it over SSH before telling me
                        <span className="muted block text-xs">A command on the machine it runs on, tried up to a few times; the push comes only if none of them brings it back.</span>
                    </span>
                </label>
                {form.restarts ? (
                    <div className="mt-3 grid gap-3 sm:grid-cols-[1fr_2fr_auto]">
                        <div>
                            <label className="label" htmlFor="restart-target">Machine</label>
                            <input id="restart-target" className="input-mono mt-1" value={form.restartTarget} onChange={set("restartTarget")} placeholder="david@pi" required />
                        </div>
                        <div>
                            <label className="label" htmlFor="restart-command">Command</label>
                            <input id="restart-command" className="input-mono mt-1" value={form.restartCommand} onChange={set("restartCommand")} placeholder="~/waha/restart-waha.sh" required maxLength={500} />
                        </div>
                        <div>
                            <label className="label" htmlFor="restart-attempts">Attempts</label>
                            <input id="restart-attempts" className="input mt-1 w-full sm:w-16" inputMode="numeric" value={form.restartAttempts} onChange={set("restartAttempts")} required />
                        </div>
                    </div>
                ) : null}
            </div>
            <div className="flex justify-end gap-2 pt-1">
                <button type="button" className="btn-ghost" onClick={onCancel}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={busy}>{busy ? <Spinner size={14} /> : null} Save</button>
            </div>
        </form>
    );
}

export function Status() {
    const [data, setData] = useState(null);
    const [checking, setChecking] = useState(false);
    const [editor, setEditor] = useState(null);
    const [kinds, setKinds] = useState({});
    const [busy, setBusy] = useState(false);
    const [{ q: query, state, open, component }, setUrl] = useUrlStates({ q: "", state: "", open: "", component: "" });
    const [day, setDay] = useState(null);
    const toast = useToast();
    const now = Date.now();

    // A tap opens a day's card on a phone; a tap anywhere else closes it.
    useEffect(() => {
        if (!day) return undefined;
        const away = (event) => !event.target.closest?.("[data-bars], [data-minibars]") && setDay(null);
        document.addEventListener("pointerdown", away);
        return () => document.removeEventListener("pointerdown", away);
    }, [day]);

    const load = () => api.status.get().then(setData).catch(() => setData((value) => value ?? { state: "unknown", summary: {}, groups: [], incidents: { open: [], recent: [] } }));

    // Follows the monitor rather than a clock: the pass says when it has
    // landed, and the page reads it then — and only then, at fifty kilobytes.
    useLive(["status"], load);

    // After anything is changed, ask now rather than at the next half minute,
    // and show the answer — the server starts the pass, this waits for it.
    const reload = async () => {
        await api.status.check().catch(() => {});
        await load();
    };

    const filtering = Boolean(query.trim() || state);
    const shown = useMemo(() => {
        const needle = query.trim().toLowerCase();
        return (data?.groups ?? [])
            .map((entry) => ({
                ...entry,
                components: entry.components.filter((row) => {
                    if (state && row.state !== state) return false;
                    if (needle && !`${row.name} ${row.probe} ${entry.label}`.toLowerCase().includes(needle)) return false;
                    return true;
                })
            }))
            .filter((entry) => entry.components.length > 0);
    }, [data, query, state]);

    const counts = useMemo(() => {
        const all = (data?.groups ?? []).flatMap((entry) => entry.components);
        return {
            all: all.length,
            down: all.filter((row) => row.state === "down").length,
            failing: all.filter((row) => row.state === "failing").length,
            paused: all.filter((row) => row.state === "paused").length
        };
    }, [data]);

    // Which projects are open: the ones in the URL, the one holding the expanded
    // component, and — while searching or filtering — every one that matched.
    const openKeys = useMemo(() => new Set(open.split(",").filter(Boolean)), [open]);
    const componentGroup = component ? (data?.groups ?? []).find((entry) => entry.components.some((row) => row.id === component))?.key : null;
    const isOpen = (key) => filtering || openKeys.has(key) || key === componentGroup;
    const allOpen = shown.length > 0 && shown.every((entry) => isOpen(entry.key));
    const toggleGroup = (key) => {
        const next = new Set(openKeys);
        if (isOpen(key)) {
            next.delete(key);
            if (key === componentGroup) setUrl("component", "");
        } else next.add(key);
        setUrl("open", [...next].join(","));
    };
    const toggleAll = () => setUrl("open", allOpen ? "" : shown.map((entry) => entry.key).join(","));

    const incidents = useMemo(() => [...(data?.incidents.open ?? []), ...(data?.incidents.recent ?? [])], [data]);
    const shownIncidents = useMemo(() => {
        const needle = query.trim().toLowerCase();
        return incidents.filter((incident) => {
            if (state === "down" && incident.endedAt) return false;
            if (needle && !`${incident.name} ${incident.group}`.toLowerCase().includes(needle)) return false;
            return true;
        });
    }, [incidents, query, state]);

    const openEditor = async (mode, row = null) => {
        try {
            const list = await api.monitors.list();
            setKinds(list.kinds);
            const monitor = row ? list.monitors.find((entry) => `monitor:${entry.id}` === row.id) : null;
            if (mode === "edit" && !monitor) throw new Error("that monitor is gone");
            setEditor({ mode, monitor });
        } catch (error) {
            toast(error.message, "error");
        }
    };

    const act = async (row, mode) => {
        if (mode === "edit") return openEditor("edit", row);
        const id = row.id.slice("monitor:".length);
        try {
            if (mode === "delete") {
                if (!window.confirm(`Stop watching ${row.name}? Its history stays until it expires.`)) return undefined;
                await api.monitors.remove(id);
                toast(`${row.name} is no longer watched`);
            } else {
                await api.monitors.update(id, { enabled: mode === "resume" });
                toast(mode === "resume" ? `${row.name} is being asked again` : `${row.name} is paused`);
            }
            await reload();
        } catch (error) {
            toast(error.message, "error");
        }
        return undefined;
    };

    const save = async (form) => {
        const http = form.kind === "http";
        const headerName = form.headerName.trim();
        const body = {
            name: form.name.trim(),
            kind: form.kind,
            target: form.target.trim(),
            group: form.group.trim(),
            expect: {
                status: http && form.expectStatus !== "" ? Number(form.expectStatus) : null,
                json: http && form.jsonPath.trim() ? { path: form.jsonPath.trim(), equals: form.jsonEquals } : null
            },
            restart: form.restarts
                ? { kind: "ssh", target: form.restartTarget.trim(), command: form.restartCommand.trim(), attempts: Number(form.restartAttempts) || 3 }
                : null
        };
        // Headers only travel when they change: a new value, or the name cleared to
        // remove one. Untouched, the sealed key stays where it is.
        if (http && headerName && form.headerValue) body.headers = { [headerName]: form.headerValue };
        else if (form.hasHeader && (!headerName || !http)) body.headers = {};
        setBusy(true);
        try {
            if (editor.monitor) await api.monitors.update(editor.monitor.id, body);
            else await api.monitors.create(body);
            toast(`${body.name} is being watched`);
            setEditor(null);
            await reload();
        } catch (error) {
            toast(error.message, "error");
        } finally {
            setBusy(false);
        }
    };

    if (!data) return <Spinner className="muted" />;

    const total = counts.all;
    const shownCount = shown.reduce((sum, entry) => sum + entry.components.length, 0);

    return (
        <div className="space-y-4">
            <PageHeader
                title="Status"
                meta={`${total} components`}
                actions={
                    <>
                        <button
                            className="btn-ghost"
                            disabled={checking}
                            onClick={async () => {
                                setChecking(true);
                                try {
                                    const result = await api.status.check();
                                    await load();
                                    toast(`Asked ${result.checked} components · ${result.down} not answering`, result.down > 0 ? "error" : "info");
                                } catch (error) {
                                    toast(error.message, "error");
                                } finally {
                                    setChecking(false);
                                }
                            }}
                        >
                            <RefreshCw size={14} className={checking ? "animate-spin" : ""} /> {checking ? "Checking…" : "Check now"}
                        </button>
                        <button className="btn-primary" onClick={() => openEditor("create")}>
                            <Plus size={14} /> Add monitor
                        </button>
                    </>
                }
            />

            <Banner data={data} />

            <StatSummary
                stats={[
                    { label: "Components", value: data.summary.total ?? 0 },
                    { label: "Not answering", value: data.summary.down ?? 0, tone: data.summary.down > 0 ? "red" : null },
                    { label: "Uptime 30d", value: percent(data.summary.uptime30), tone: data.summary.uptime30 !== null && data.summary.uptime30 < 99 ? "amber" : null },
                    { label: "Open incidents", value: data.summary.openIncidents ?? 0, tone: data.summary.openIncidents > 0 ? "red" : null }
                ]}
            />

            <Toolbar
                query={query}
                onQuery={(value) => setUrl("q", value)}
                state={state}
                onState={(value) => setUrl("state", value)}
                counts={counts}
                allOpen={allOpen}
                onToggleAll={toggleAll}
            />

            {shown.length === 0 ? (
                <div className="card">
                    <EmptyState title={total === 0 ? "Nothing checked yet" : "Nothing matches"} hint={total === 0 ? "The first pass runs within half a minute of ops starting." : "Clear the search or the filter."} />
                </div>
            ) : (
                <div className="card overflow-hidden">
                    {filtering ? <p className="muted border-b border-gray-100 px-4 py-2 text-xs dark:border-gray-800">{shownCount} of {total} components, in {shown.length} project{shown.length === 1 ? "" : "s"}</p> : null}
                    <ul className="divide-y divide-gray-100 dark:divide-gray-800">
                        {shown.map((entry) => (
                            <GroupRow
                                key={entry.key}
                                group={entry}
                                open={isOpen(entry.key)}
                                onToggle={() => toggleGroup(entry.key)}
                                component={component}
                                onComponent={(id) => setUrl("component", id)}
                                onEdit={act}
                                onDay={setDay}
                                now={now}
                            />
                        ))}
                    </ul>
                </div>
            )}

            <section className="space-y-2">
                <h2 className="section-title">Incidents</h2>
                <DataTable
                    rows={shownIncidents}
                    rowKey={(incident) => incident.id}
                    caption="Incidents, open first"
                    onRowClick={(incident) => setUrl("component", incident.target)}
                    empty={
                        <div className="card">
                            <EmptyState title="No incidents" hint="Nothing has stopped answering since the monitor started." />
                        </div>
                    }
                    columns={[
                        {
                            key: "name",
                            header: "Component",
                            primary: true,
                            cell: (incident) => (
                                <span className="flex items-center gap-2">
                                    {incident.endedAt ? <StatusDot status="up" title="resolved" /> : <span className="inline-block size-2 shrink-0 animate-pulse rounded-full bg-red-500" title="open" />}
                                    <span className="font-medium">{incident.name}</span>
                                    <span className="muted text-xs">{incident.group}</span>
                                </span>
                            ),
                            // Two lines on a phone: the line is one sentence wide.
                            mobileCell: (incident) => (
                                <span className="flex min-w-0 items-center gap-2">
                                    {incident.endedAt ? <StatusDot status="up" title="resolved" /> : <span className="inline-block size-2 shrink-0 animate-pulse rounded-full bg-red-500" title="open" />}
                                    <span className="min-w-0">
                                        <span className="block truncate font-medium">{incident.name}</span>
                                        <span className="muted block truncate text-xs">{incident.group}</span>
                                    </span>
                                </span>
                            )
                        },
                        { key: "started", header: "Started", summary: true, cell: (incident) => <span className="tabular-nums" title={incident.startedAt}>{ago(incident.startedAt)}</span> },
                        {
                            key: "duration",
                            header: "Duration",
                            summary: true,
                            align: "right",
                            cell: (incident) => <span className="whitespace-nowrap tabular-nums">{incident.endedAt ? humanDuration(incident.durationMs) : `${humanDuration(incident.durationMs)} and counting`}</span>,
                            mobileCell: (incident) => <span className="text-xs tabular-nums">{humanDuration(incident.durationMs)}{incident.endedAt ? "" : "…"}</span>
                        },
                        { key: "reason", header: "Reason", hideBelow: "lg", cell: (incident) => <span className="muted text-xs">{incident.reason}</span> }
                    ]}
                />
            </section>

            <DayPopover anchor={day?.anchor} day={day?.day} />

            <Modal open={Boolean(editor)} onClose={() => setEditor(null)} title={editor?.monitor ? `Edit ${editor.monitor.name}` : "Add a monitor"}>
                {editor ? (
                    <MonitorForm
                        kinds={kinds}
                        busy={busy}
                        onCancel={() => setEditor(null)}
                        onSubmit={save}
                        initial={editor.monitor
                            ? {
                                name: editor.monitor.name,
                                kind: editor.monitor.kind,
                                target: editor.monitor.target,
                                group: editor.monitor.group,
                                expectStatus: editor.monitor.expect?.status ?? "",
                                jsonPath: editor.monitor.expect?.json?.path ?? "",
                                jsonEquals: editor.monitor.expect?.json?.equals ?? "",
                                headerName: editor.monitor.headerNames?.[0] ?? "",
                                headerValue: "",
                                hasHeader: (editor.monitor.headerNames?.length ?? 0) > 0,
                                restarts: Boolean(editor.monitor.restart),
                                restartTarget: editor.monitor.restart?.target ?? "",
                                restartCommand: editor.monitor.restart?.command ?? "",
                                restartAttempts: String(editor.monitor.restart?.attempts ?? 3)
                            }
                            : EMPTY_FORM}
                    />
                ) : null}
            </Modal>
        </div>
    );
}
